import {
  DEFAULT_MEMBERS_SORT_BY,
  DEFAULT_TEAM_MEMBERS_SORT_BY,
} from "@components/table/members/members-sort-constants";
import { MembersSortField } from "@components/table/members/members-table-constants";
import {
  compareMembersByDefaultName,
  compareMembersByStatus,
} from "@components/table/members/members-table-utils";
import { UserId } from "@custom-types/base-types";
import { isGroupMember } from "@custom-types/member-type-guards";
import {
  MemberRoleTypes,
  MembersSortBy,
  PartialSdbMemberTypes,
  SdbMemberTypes,
  UserRoleEnum,
} from "@custom-types/member-types";
import { SortItem } from "@custom-types/sort-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { StringEntityId } from "@store/store-types";
import { getPrettyName } from "@utils/user-utils";
import { cloneElement, JSX } from "react";

/** Gets the ID for a member to be used in the entities dictionary to identify each member  */
export function getMemberId(member: PartialSdbMemberTypes): StringEntityId {
  return member.id ?? member.identity ?? member.email ?? "";
}

interface GetMemberByIdProps {
  /** ID of the member */
  memberId?: string;

  /** List of company members */
  companyMembers: SdbMemberTypes[];

  /** List of project members */
  projectMembers: SdbMemberTypes[];
}

/** Returns the member entity by the member ID if it's available */
export function getMemberById({
  memberId,
  companyMembers,
  projectMembers,
}: GetMemberByIdProps): SdbMemberTypes | undefined {
  const companyMember = companyMembers.find(
    (member) => member.identity === memberId
  );

  const projectMember = projectMembers.find(
    (member) => member.identity === memberId
  );

  // Give priority to the company level member entity
  return companyMember || projectMember;
}

/** Returns the get the formatted member name by the member ID if it's available */
export function getMemberNameById({
  memberId,
  companyMembers,
  projectMembers,
}: GetMemberByIdProps): string | undefined {
  const member = getMemberById({
    memberId,
    companyMembers,
    projectMembers,
  });

  const name = getPrettyName(member);

  return name !== "" ? name : undefined;
}

/** Returns the email by the member ID if it's available */
export function getMemberEmailById({
  memberId,
  companyMembers,
  projectMembers,
}: GetMemberByIdProps): string | undefined {
  const member = getMemberById({
    memberId,
    companyMembers,
    projectMembers,
  });

  return member?.email;
}

/** Returns a map of members accessible by id */
export function getMembersMapById(
  companyMembers: SdbMemberTypes[]
): Map<UserId, SdbMemberTypes> {
  return new Map(companyMembers.map((member) => [member.identity, member]));
}

/** Returns the higher role between two role provided */
export function getHigherRole(
  roleA: MemberRoleTypes,
  roleB: MemberRoleTypes
): MemberRoleTypes {
  return UserRoleEnum[roleA] >= UserRoleEnum[roleB] ? roleA : roleB;
}

/**
 * Creates an export button component with standard button type if there are members and an export component is available.
 * Generic helper to avoid repeating the same pattern across multiple files.
 *
 * @param itemCount - The count of items (members, groups, etc.) to check if export should be enabled
 * @param exportComponent - The export component from bulk actions
 * @returns The cloned export component with standard button type, or undefined if no items or component
 */
export function getExportComponent(
  itemCount: number,
  exportComponent: JSX.Element | null | undefined
): JSX.Element | undefined {
  return itemCount > 0 && exportComponent
    ? cloneElement(exportComponent, { buttonType: "standard" })
    : undefined;
}

/**
 * Sorts an array of members by their name or email.
 * Teams(user-group) are prioritized over individual users.
 * For individual users, if the name is available, it will be used for sorting; otherwise, the email will be used.
 *
 * @param members - The array of members to sort
 * @returns A new sorted array of members
 */
export function sortMembersByNameOrEmail<
  T extends FaroCoreApiTypes.ProjectMemberBase | SdbMemberTypes,
>(members: T[]): T[] {
  return members.sort((a, b) => {
    const isAGroup = a.kind === "user-group";
    const isBGroup = b.kind === "user-group";
    if (isAGroup !== isBGroup) {
      return isAGroup ? -1 : 1;
    }

    const aName = a.kind === "user" && a.identity !== a.id ? a.email : a.name;
    const bName = b.kind === "user" && b.identity !== b.id ? b.email : b.name;

    return (aName ?? "").localeCompare(bName ?? "");
  });
}

/**
 * Sorts an array of members based on the provided sorting criteria.
 * @param members - The array of members to sort
 * @param sortBy - The sorting criteria
 * @returns A new sorted array of members
 */
export function sortMembers(
  members: SdbMemberTypes[] | FaroCoreApiTypes.GroupMember[],
  sortBy: MembersSortBy
): SdbMemberTypes[] {
  const sorted = [...members];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case "user_ascending":
      case "name_ascending":
        return compareMembersByDefaultName(a, b, "ascending");
      case "user_descending":
      case "name_descending":
        return compareMembersByDefaultName(a, b, "descending");
      case "email_ascending":
        return a.email.localeCompare(b.email);
      case "email_descending":
        return b.email.localeCompare(a.email);
      case "status_ascending":
        return compareMembersByStatus(a, b, "ascending");
      case "status_descending":
        return compareMembersByStatus(a, b, "descending");
      case "role_ascending":
        return UserRoleEnum[b.role] - UserRoleEnum[a.role];
      case "role_descending":
        return UserRoleEnum[a.role] - UserRoleEnum[b.role];
      case "joinedDate_ascending":
        if (isGroupMember(a) && isGroupMember(b)) {
          return (
            new Date(a.dateJoined).getTime() - new Date(b.dateJoined).getTime()
          );
        }
        return 0;
      case "joinedDate_descending":
        if (isGroupMember(a) && isGroupMember(b)) {
          return (
            new Date(b.dateJoined).getTime() - new Date(a.dateJoined).getTime()
          );
        }
        return 0;
      default:
        return 0;
    }
  });

  return sorted;
}

/** Converts a SortItem to a MembersSortBy value */
export function getMembersSortBy(
  sortItem: SortItem<MembersSortField>
): MembersSortBy {
  const { field, sort } = sortItem;
  const isAscending = sort === "asc";
  switch (field) {
    case "user":
      return isAscending ? "user_ascending" : "user_descending";
    case "name":
      return isAscending ? "name_ascending" : "name_descending";
    case "email":
      return isAscending ? "email_ascending" : "email_descending";
    case "status":
      return isAscending ? "status_ascending" : "status_descending";
    case "role":
      return isAscending ? "role_ascending" : "role_descending";
    case "joinedDate":
      return isAscending ? "joinedDate_ascending" : "joinedDate_descending";
    default:
      return DEFAULT_MEMBERS_SORT_BY;
  }
}

/** Converts a SortItem to a TeamMemberSortBy value */
export function getTeamMembersSortBy(
  sortItem: SortItem<MembersSortField>
): FaroCoreApiTypes.TeamMemberSortBy {
  if (sortItem.field === "joinedDate") {
    return sortItem.sort === "asc" ? "oldest_first" : "newest_first";
  }

  return DEFAULT_TEAM_MEMBERS_SORT_BY;
}
